import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths, helpContextPageUrl} from '../../../conf/Routes';
import {ServerGroupGetSchema} from '../../../api/DBSchema/Server';
import generalIcon from '../../../assets/images/general.png';
import historyIcon from '../../../assets/images/history.png';
import ServerGroupGeneral from './InnerPages/ServerGroupGeneral';
import ServerGroupHistory from './InnerPages/ServerGroupHistory';

export function useServerGroupEditor(serverGroupId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origServerGroupObject, setOrigServerGroupObject] = useState({});
    const [serverGroupObject, setServerGroupObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: ServerGroupGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: ServerGroupGeneral, helpId: helpContextPageUrl.serverGroupGeneral },
        { id: ServerGroupGetSchema.pages.history.pageId, title: t('SMP.HISTORY'), icon: historyIcon, pageElement: ServerGroupHistory, helpId: helpContextPageUrl.serverGroupHistory }
    ]), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);
    
    const fetchServerGroup = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchServerGroupResp = await Api.fetchSingleObject(ObjectTypes.SERVER_GROUP, serverGroupId);
            const serverGroup = fetchServerGroupResp.data.data;
            // Validate server group response
            if (!serverGroup) {
                console.error('Get server group response failed: server response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigServerGroupObject(serverGroup);
            setServerGroupObject(serverGroup);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [serverGroupId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setServerGroupData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setServerGroupObject(serverGroupObj => produce(serverGroupObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.SERVER_GROUP, serverGroupId, serverGroupObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [serverGroupId, getMessagesArrayByPages, serverGroupObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.SERVER_GROUP, serverGroupId, serverGroupObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchServerGroup();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchServerGroup, getErrorMessage, serverGroupId, serverGroupObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [serverGroupId] ))) {
            Api.deleteObjects(ObjectTypes.SERVER_GROUP, [serverGroupId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.servers));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [serverGroupId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [serverGroupId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setServerGroupObject(origServerGroupObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origServerGroupObject]);

    const reloadButtonOnClick = useCallback(() => {
        fetchServerGroup();
        enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_REFRESHED_OK'));
    }, [fetchServerGroup, enqueueSuccessSnackbar, t]);

    // Fetch server on loading of page
    useEffect(() => {
        fetchServerGroup();
    }, [fetchServerGroup]);
    
    return {
        serverGroupObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setServerGroupData,
        reloadButtonOnClick,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchServerGroup
    };
}